<!-- Modern Payment History Dashboard -->
<div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">

  <!-- Loading State -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-20">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
    <p class="text-gray-600">Loading payment history...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="flex flex-col items-center justify-center py-20">
    <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <mat-icon class="text-red-500 text-3xl">error</mat-icon>
    </div>
    <h3 class="text-lg font-semibold text-gray-900 mb-2">Error Loading Payments</h3>
    <p class="text-gray-500 mb-4">{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadSubscriptions()">
      <mat-icon class="mr-2">refresh</mat-icon>
      Try Again
    </button>
  </div>

  <!-- Main Content -->
  <div *ngIf="!loading && !error">

    <!-- Header Section -->
    <div class="mb-8">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <mat-icon class="text-white">payments</mat-icon>
            </div>
            Payment History
          </h1>
          <p class="text-gray-600">View all your subscription payments and download invoices</p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3">
          <button
            mat-stroked-button
            (click)="loadSubscriptions()"
            class="!border-gray-300 !text-gray-700 hover:!bg-gray-50"
          >
            <mat-icon class="mr-1">refresh</mat-icon>
            Refresh
          </button>
          <button
            mat-raised-button
            color="primary"
            (click)="openAddDialog()"
            class="!shadow-lg"
          >
            <mat-icon class="mr-1">add</mat-icon>
            New Payment
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <!-- Total Payments -->
      <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <mat-icon class="text-blue-600">receipt_long</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-500 uppercase tracking-wide">Total Payments</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalPayments }}</p>
          </div>
        </div>
      </div>

      <!-- Total Amount -->
      <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <mat-icon class="text-emerald-600">currency_rupee</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-500 uppercase tracking-wide">Total Amount</p>
            <p class="text-2xl font-bold text-gray-900">₹{{ totalAmount | number:'1.0-0' }}</p>
          </div>
        </div>
      </div>

      <!-- Active Subscriptions -->
      <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <mat-icon class="text-green-600">check_circle</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-500 uppercase tracking-wide">Active</p>
            <p class="text-2xl font-bold text-green-600">{{ activeCount }}</p>
          </div>
        </div>
      </div>

      <!-- Staff Onboarded -->
      <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <mat-icon class="text-purple-600">group</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-500 uppercase tracking-wide">Staff Onboarded</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalStaff }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <mat-icon class="text-gray-400">search</mat-icon>
        </div>
        <input
          type="text"
          placeholder="Search by invoice ID, amount, or status..."
          class="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all"
          (input)="applyFilter($event)"
        />
      </div>
    </div>

    <!-- Payment Cards Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" *ngIf="subscriptions.length > 0">
      <div
        *ngFor="let sub of subscriptions"
        class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
      >
        <!-- Card Header -->
        <div class="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <mat-icon class="text-indigo-600">receipt</mat-icon>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900">Invoice #INV-{{ sub.id }}</h3>
                <p class="text-sm text-gray-500">{{ sub.created_at | date:'MMM dd, yyyy' }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <!-- Status Badge -->
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                [ngClass]="{
                  'bg-green-100 text-green-800': getStatusText(sub) === 'Active',
                  'bg-yellow-100 text-yellow-800': sub.statusDetails?.gracePeriodRemaining > 0,
                  'bg-red-100 text-red-800': getStatusText(sub) === 'Expired'
                }"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1">{{ getStatusIcon(sub) }}</mat-icon>
                {{ getStatusText(sub) }}
              </span>
              <!-- Menu -->
              <button mat-icon-button [matMenuTriggerFor]="menu" class="!w-8 !h-8">
                <mat-icon class="text-gray-400">more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="downloadInvoice(sub)">
                  <mat-icon>download</mat-icon>
                  <span>Download Invoice</span>
                </button>
                <button mat-menu-item (click)="viewDetails(sub)">
                  <mat-icon>visibility</mat-icon>
                  <span>View Details</span>
                </button>
              </mat-menu>
            </div>
          </div>
        </div>

        <!-- Card Body -->
        <div class="p-6">
          <!-- Amount Section -->
          <div class="flex items-center justify-between mb-6">
            <div>
              <p class="text-sm text-gray-500 mb-1">Total Amount</p>
              <p class="text-3xl font-bold text-gray-900">₹{{ sub.total_amount | number:'1.0-0' }}</p>
            </div>
            <div
              class="px-4 py-2 rounded-lg"
              [ngClass]="{
                'bg-green-50 text-green-700': sub.payment_status === 1,
                'bg-yellow-50 text-yellow-700': sub.payment_status !== 1
              }"
            >
              <div class="flex items-center gap-2">
                <mat-icon class="!text-lg">{{ getPaymentStatusIcon(sub) }}</mat-icon>
                <span class="font-medium">{{ getPaymentStatusText(sub) }}</span>
              </div>
            </div>
          </div>

          <!-- Details Grid -->
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div class="bg-gray-50 rounded-lg p-3">
              <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
              <p class="font-semibold text-gray-900">{{ sub.duration }} Months</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-3">
              <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Users</p>
              <p class="font-semibold text-gray-900">{{ getTotalUsers(sub) }} Users</p>
            </div>
          </div>

          <!-- Membership Period -->
          <div class="bg-blue-50 rounded-lg p-4 mb-6">
            <p class="text-xs text-blue-600 uppercase tracking-wide font-medium mb-3">Membership Period</p>
            <div class="flex items-center justify-between">
              <div class="text-center">
                <p class="text-xs text-gray-500 mb-1">Start Date</p>
                <p class="font-semibold text-gray-900">{{ sub.membership_start_date | date:'MMM dd, yyyy' }}</p>
              </div>
              <div class="flex-1 flex items-center justify-center px-4">
                <div class="h-px bg-blue-200 w-full relative">
                  <mat-icon class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-400 bg-blue-50 !text-sm">arrow_forward</mat-icon>
                </div>
              </div>
              <div class="text-center">
                <p class="text-xs text-gray-500 mb-1">End Date</p>
                <p class="font-semibold text-gray-900">{{ sub.membership_end_date | date:'MMM dd, yyyy' }}</p>
              </div>
            </div>
          </div>

          <!-- Status Message -->
          <div
            *ngIf="sub.statusDetails?.message"
            class="rounded-lg p-3 mb-4"
            [ngClass]="{
              'bg-green-50 border border-green-200': sub.statusDetails?.isActive,
              'bg-yellow-50 border border-yellow-200': sub.statusDetails?.gracePeriodRemaining > 0,
              'bg-red-50 border border-red-200': !sub.statusDetails?.isActive && sub.statusDetails?.gracePeriodRemaining <= 0
            }"
          >
            <div class="flex items-center gap-2">
              <mat-icon
                class="!text-lg"
                [ngClass]="{
                  'text-green-600': sub.statusDetails?.isActive,
                  'text-yellow-600': sub.statusDetails?.gracePeriodRemaining > 0,
                  'text-red-600': !sub.statusDetails?.isActive && sub.statusDetails?.gracePeriodRemaining <= 0
                }"
              >info</mat-icon>
              <p class="text-sm font-medium"
                [ngClass]="{
                  'text-green-700': sub.statusDetails?.isActive,
                  'text-yellow-700': sub.statusDetails?.gracePeriodRemaining > 0,
                  'text-red-700': !sub.statusDetails?.isActive && sub.statusDetails?.gracePeriodRemaining <= 0
                }"
              >
                {{ sub.statusDetails.message }}
              </p>
            </div>
          </div>

          <!-- Domain Modules -->
          <div class="border-t border-gray-100 pt-4">
            <p class="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">Active Modules</p>
            <div class="flex flex-wrap gap-2">
              <span
                *ngIf="sub.road && sub.road.users_count > 0"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-orange-50 text-orange-700 text-sm"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1.5">edit_road</mat-icon>
                Road ({{ sub.road.users_count }})
              </span>
              <span
                *ngIf="sub.construction && sub.construction.users_count > 0"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-sm"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1.5">business</mat-icon>
                Construction ({{ sub.construction.users_count }})
              </span>
              <span
                *ngIf="sub.utilities && sub.utilities.users_count > 0"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-yellow-50 text-yellow-700 text-sm"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1.5">power</mat-icon>
                Utilities ({{ sub.utilities.users_count }})
              </span>
              <span
                *ngIf="sub.consultancy && sub.consultancy.users_count > 0"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 text-sm"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1.5">analytics</mat-icon>
                Consultancy ({{ sub.consultancy.users_count }})
              </span>
              <span
                *ngIf="!sub.road?.users_count && !sub.construction?.users_count && !sub.utilities?.users_count && !sub.consultancy?.users_count"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm"
              >
                <mat-icon class="!text-sm !w-4 !h-4 mr-1.5">info</mat-icon>
                No modules
              </span>
            </div>
          </div>
        </div>

        <!-- Card Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <p class="text-sm text-gray-500">
            Organization: <span class="font-medium text-gray-700">{{ orgName }}</span>
          </p>
          <button
            mat-raised-button
            color="primary"
            (click)="downloadInvoice(sub)"
            class="!shadow-none"
          >
            <mat-icon class="mr-1 !text-lg">download</mat-icon>
            Download Invoice
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      *ngIf="subscriptions.length === 0"
      class="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center"
    >
      <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <mat-icon class="text-gray-400 !text-4xl !w-10 !h-10">receipt_long</mat-icon>
      </div>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">No Payment History</h3>
      <p class="text-gray-500 mb-6 max-w-md mx-auto">
        You haven't made any subscription payments yet. Subscribe to a plan to get started.
      </p>
      <button mat-raised-button color="primary" (click)="openAddDialog()">
        <mat-icon class="mr-1">add</mat-icon>
        Make First Payment
      </button>
    </div>
  </div>
</div>
